/**
 * Which PLAY FLOW a /playChess (or /CCTournaments) request is, and therefore
 * which lane can serve it. Pure, so it is unit-testable and cheap enough to
 * run on every mount.
 *
 * Rules, FIRST MATCH WINS (plan "The route switch"):
 *   0. `royaleTournamentId` present -> the tournament params belong to Royale,
 *      not to us: ignore them and keep resolving;
 *   1. tournamentId | from | isJoined param -> 'tournament';
 *   2. an active tournament in the store    -> 'tournament';
 *   3. game phase starting|playing          -> 'live';
 *   4. challengePacket handshake            -> 'challengeAccept';
 *   5. club_challenge_role handshake        -> 'clubChallenge';
 *   6. league_opp_id handshake              -> 'leagueChallenge';
 *   7. pendingAutoJoin                      -> 'autoJoin';
 *   8. otherwise                            -> 'lobby'.
 *
 * `lane` is 'new' only when the flow is in `supported`; everything else falls
 * back to the untouched old screen.
 */
import type {Handshakes} from '@/Store/liveGame/handshakes';
import type {LiveGameState} from '@/Store/liveGameStore';

import type {Lane} from './playFlags';

export type Flow =
  | 'tournament'
  | 'live'
  | 'challengeAccept'
  | 'clubChallenge'
  | 'leagueChallenge'
  | 'autoJoin'
  | 'lobby';

/**
 * Flows the V2 lane can serve today. Grows one milestone at a time.
 *
 * M1b adds 'live' (same-tab re-entry / a reload into a running casual game: the
 * store is already in starting|playing, and `selectRailState` puts the Stage and
 * the Rail up) and 'autoJoin' (`hooks/useAutoJoin` consumes the dashboard's
 * intent and seeks).
 *
 * M2 Task B adds 'challengeAccept': the app-wide `Components/ChallengeComponent`
 * has already SENT `challenge_accept_response` and left its terms in the
 * `challengePacket` handshake, so all this lane has to do is consume the key
 * (`useGameplayHandlers`) and let `start_game_response` start the game exactly as
 * a quick-play match does.
 *
 * M4 adds 'tournament' — a `/CCTournaments?tournamentId=` deep link, a reload
 * inside a tournament game, and the lobby's Tournaments tab (which now
 * NAVIGATES instead of handing the lane back). The switch resolves once per
 * mount from `route.params` (index.tsx L37-68) and `ScreenName.Tournaments` is a
 * DIFFERENT route from `/playChess`, so a navigate from the tab mounts a fresh
 * switch — no `nextDecision`-style re-resolution is needed here. Club and league
 * challenges stay on the old lane (M5).
 */
export const SUPPORTED_FLOWS: Flow[] = [
  'lobby',
  'live',
  'autoJoin',
  'challengeAccept',
  'tournament',
];

export type ResolveFlowInput = {
  params?: any;
  handshakes?: Partial<Handshakes> | null;
  storeSnapshot?: Pick<LiveGameState, 'game' | 'tournament'> | null;
  supported?: Flow[];
};

export function resolveFlow({
  params,
  handshakes,
  storeSnapshot,
  supported = SUPPORTED_FLOWS,
}: ResolveFlowInput): {lane: Lane; flow: Flow} {
  const flow = pickFlow(params, handshakes, storeSnapshot);
  return {lane: supported.includes(flow) ? 'new' : 'old', flow};
}

function pickFlow(
  params: any,
  handshakes: Partial<Handshakes> | null | undefined,
  storeSnapshot: Pick<LiveGameState, 'game' | 'tournament'> | null | undefined,
): Flow {
  // A Royale deep link carries its own tournament params; PlayChess ignores
  // them (Appendix A F14), so they must not route us into the tournament flow.
  const routeParams = params?.royaleTournamentId ? {} : params || {};

  if (
    routeParams.tournamentId ||
    routeParams.from ||
    routeParams.isJoined !== undefined
  ) {
    return 'tournament';
  }
  if (storeSnapshot?.tournament?.id) {
    return 'tournament';
  }
  const phase = storeSnapshot?.game?.phase;
  if (phase === 'starting' || phase === 'playing') {
    return 'live';
  }
  if (handshakes?.challengePacket) {
    return 'challengeAccept';
  }
  if (handshakes?.club?.role) {
    return 'clubChallenge';
  }
  if (handshakes?.league?.oppId) {
    return 'leagueChallenge';
  }
  if (handshakes?.pendingAutoJoin) {
    return 'autoJoin';
  }
  return 'lobby';
}
